import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Palette,
  Loader2,
  Shield,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import { Switch } from "../../components/ui/switch";

const TONE = {
  active: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  suspended: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function AdminHospitalDetail() {
  const { id } = useParams();

  const [h, setH] = useState(null);
  const [theme, setTheme] = useState(null);

  // Payment feature
  const [paymentFeatureEnabled, setPaymentFeatureEnabled] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentErr, setPaymentErr] = useState("");

  // Theme
  const [themeBusy, setThemeBusy] = useState(false);
  const [themeErr, setThemeErr] = useState("");

  useEffect(() => {
    loadHospital();
    loadTheme();

  }, [id]);

  const loadHospital = async () => {
  try {
    const { data } = await api.get(`/admin/hospitals/${id}`);
    setH(data);
    setPaymentFeatureEnabled(!!data.payments_feature_enabled);
    setPaymentLoading(false);
  } catch (e) {
    setPaymentLoading(false);
    toast.error(apiError(e, "Failed to load hospital"));
  }
};

  const loadTheme = async () => {
    try {
      const { data } = await api.get(`/admin/hospitals/${id}/theme`);
      setTheme(data);
    } catch {
      // Theme may not be configured yet.
    }
  };


const togglePaymentFeature = async (nextVal) => {
  setPaymentBusy(true);
  setPaymentErr("");

  try {
    const { data } = await api.patch(
      `/admin/hospitals/${id}/payment-feature`,
      {
        enabled: nextVal,
      }
    );

    const enabled =
      data?.hospital?.payments_feature_enabled ?? nextVal;

    setPaymentFeatureEnabled(!!enabled);

    setH((prev) =>
      prev
        ? {
            ...prev,
            payments_feature_enabled: !!enabled,
            online_payments_enabled:
              data?.hospital?.online_payments_enabled ??
              prev.online_payments_enabled,
          }
        : prev
    );

    toast.success(
      enabled
        ? "Payments enabled for this hospital"
        : "Payments disabled for this hospital"
    );
  } catch (e) {
    setPaymentErr(
      apiError(e, "Failed to update payment feature")
    );
  } finally {
    setPaymentBusy(false);
  }
};

  const toggleOverride = async (nextVal) => {
    setThemeBusy(true);
    setThemeErr("");

    try {
      const { data } = await api.patch(
        `/admin/hospitals/${id}/theme`,
        {
          super_admin_override_disabled: nextVal,
        }
      );

      setTheme(data);

      toast.success(
        nextVal
          ? "Custom theme disabled for this hospital"
          : "Override lifted — hospital can use custom theme"
      );
    } catch (e) {
      setThemeErr(
        apiError(e, "Failed to update override")
      );
    } finally {
      setThemeBusy(false);
    }
  };

  if (!h) {
    return (
      <div className="text-sm text-mb-fg2">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="space-y-6 max-w-4xl"
      data-testid="admin-hospital-detail"
    >
      {/* Back */}
      <Link
        to="/admin/hospitals"
        className="text-sm text-mb-fg2 hover:text-white inline-flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        All hospitals
      </Link>

      {/* Hospital Header */}
      <div className="rounded-xl border border-mb-border bg-mb-surface p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">
          ◖ hospital
        </div>

        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-light tracking-tight"
              data-testid="admin-hospital-name"
            >
              {h.name}
            </h1>

            <div className="mt-1 text-xs text-mb-fg2 uppercase tracking-[0.2em]">
              {h.slug}
            </div>

            {h.address && (
              <div className="mt-3 text-sm text-mb-fg2 inline-flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {h.address}
              </div>
            )}
          </div>

          <span
            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${
              TONE[h.status] || ""
            }`}
          >
            {h.status}
          </span>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat
          label="Doctors"
          value={h.doctor_count}
          testid="hd-doctors"
        />

        <Stat
          label="Admins"
          value={h.admin_count}
          testid="hd-admins"
        />

        <Stat
          label="Appointments"
          value={h.appointment_count}
          testid="hd-appointments"
        />

        <Stat
          label="Net revenue"
          value={formatCurrency(h.net_revenue, {
            decimals: true,
          })}
          testid="hd-net-revenue"
        />
      </div>

      {/* Revenue / Hospital Details */}
      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid grid-cols-2 gap-4 text-sm">
        <Field
          label="Gross revenue"
          value={formatCurrency(h.revenue, {
            decimals: true,
          })}
          testid="hd-gross"
        />

        <Field
          label="Refunded"
          value={formatCurrency(h.refunded, {
            decimals: true,
          })}
          testid="hd-refunded"
        />

        <Field
          label="Created"
          value={new Date(h.created_at).toLocaleDateString()}
          testid="hd-created"
        />

        <Field
          label="ID"
          value={h.id}
          testid="hd-id"
          mono
        />
      </div>

      {/* ====================================================== */}
      {/* PAYMENT FEATURE CONTROL */}
      {/* ====================================================== */}

      <div
        className="rounded-xl border border-mb-border bg-mb-surface p-6 space-y-5"
        data-testid="payment-feature-panel"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400 inline-flex items-center gap-2">
              <CreditCard className="h-3 w-3" />
              Payment feature
            </div>

            <div className="mt-2 text-lg font-light">
              Hospital online payments
            </div>

            <div className="text-xs text-mb-fg2 mt-1 max-w-xl leading-5">
              Allow this hospital to configure its own payment
              gateway and receive patient payments directly into
              the hospital&apos;s payment account.
            </div>
          </div>

          <div className="shrink-0">
            {paymentLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-mb-fg2" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div
                    className={`text-xs font-medium ${
                      paymentFeatureEnabled
                        ? "text-teal-400"
                        : "text-mb-fg2"
                    }`}
                  >
                    {paymentFeatureEnabled
                      ? "ENABLED"
                      : "DISABLED"}
                  </div>

                  <div className="text-[9px] uppercase tracking-[0.15em] text-mb-fg2 mt-1">
                    Super Admin
                  </div>
                </div>

                <Switch
                  checked={paymentFeatureEnabled}
                  onCheckedChange={togglePaymentFeature}
                  disabled={paymentBusy}
                  data-testid="payment-feature-switch"
                />
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        {!paymentLoading && (
          <div
            className={`rounded-lg border p-4 ${
              paymentFeatureEnabled
                ? "border-teal-500/20 bg-teal-500/5"
                : "border-mb-border bg-mb-surface2"
            }`}
          >
            <div className="flex items-start gap-3">
              <CreditCard
                className={`h-5 w-5 mt-0.5 ${
                  paymentFeatureEnabled
                    ? "text-teal-400"
                    : "text-mb-fg2"
                }`}
              />

              <div>
                <div className="text-sm text-white">
                  {paymentFeatureEnabled
                    ? "Payment integration available"
                    : "Payment integration disabled"}
                </div>

                <div className="text-xs text-mb-fg2 mt-1 leading-5">
                  {paymentFeatureEnabled
                    ? "Hospital administrators can access Payment Integration settings and configure their own gateway."
                    : "Hospital administrators cannot configure or use online payments. Patients should not be offered online payment for this hospital."}
                </div>
              </div>
            </div>
          </div>
        )}

        {paymentBusy && (
          <div className="text-xs text-mb-fg2 inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Updating payment settings…
          </div>
        )}

        {paymentErr && (
          <div
            className="text-sm text-red-400"
            data-testid="payment-feature-error"
          >
            {paymentErr}
          </div>
        )}
      </div>

      {/* ====================================================== */}
      {/* THEME OVERRIDE */}
      {/* ====================================================== */}

      <div
        className="rounded-xl border border-mb-border bg-mb-surface p-6 space-y-5"
        data-testid="theme-override-panel"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400 inline-flex items-center gap-2">
              <Palette className="h-3 w-3" />
              Theme override
            </div>

            <div className="mt-2 text-lg font-light">
              Custom theme control
            </div>

            <div className="text-xs text-mb-fg2 mt-1">
              When forced-disabled, patients see MediBooks&apos;
              default palette regardless of what this hospital has
              configured.
            </div>
          </div>

          <div className="shrink-0">
            {theme ? (
              <div className="flex items-center gap-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">
                  Force disable
                </div>

                <Switch
                  checked={
                    !!theme.super_admin_override_disabled
                  }
                  onCheckedChange={toggleOverride}
                  disabled={themeBusy}
                  data-testid="override-switch"
                />
              </div>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-mb-fg2" />
            )}
          </div>
        </div>

        {theme && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <PalettePreview
              label="Primary"
              hex={theme.primary_color}
            />

            <PalettePreview
              label="Accent"
              hex={theme.accent_color}
            />

            <PalettePreview
              label="Mode"
              hex={null}
              valueText={theme.mode}
            />

            <PalettePreview
              label="Enabled by hospital"
              hex={null}
              valueText={
                theme.is_active ? "yes" : "no"
              }
            />
          </div>
        )}

        {theme?.logo_url && (
          <div className="flex items-center gap-3 text-xs text-mb-fg2">
            <Shield className="h-3 w-3" />
            Logo:

            <img
              src={theme.logo_url}
              alt=""
              className="h-6 w-auto rounded bg-mb-surface2 border border-mb-border p-1"
            />
          </div>
        )}

        {themeErr && (
          <div
            className="text-sm text-red-400"
            data-testid="override-error"
          >
            {themeErr}
          </div>
        )}
      </div>
    </div>
  );
}

function PalettePreview({ label, hex, valueText }) {
  return (
    <div className="rounded-lg border border-mb-border bg-mb-surface2 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">
        {label}
      </div>

      <div className="mt-2 flex items-center gap-2">
        {hex && (
          <span
            className="h-5 w-5 rounded border border-mb-border"
            style={{ background: hex }}
          />
        )}

        <span className="text-sm text-white font-mono">
          {hex || valueText}
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-mb-border bg-mb-surface p-5"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
        {label}
      </div>

      <div className="mt-2 text-2xl font-light text-white">
        {value}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  testid,
}) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">
        {label}
      </div>

      <div
        className={`text-sm text-white truncate ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}